import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { enUS, it as itLocale } from "date-fns/locale";
import {
	CreditCard,
	Euro,
	Clock,
	Plus,
	Trash2,
	Receipt,
	Send,
	Ban,
	Wallet,
	Search,
	X,
	Pencil,
	RotateCcw,
	CalendarIcon,
	ArrowUp,
	ArrowDown,
	FilterX,
} from "lucide-react";

import { fetchAPI, formatMoney, formatShortDate } from "../lib/utils";
import { toLocalISODate, parseLocalDate } from "../lib/programBuilderUtils";
import { useLanguage } from "../contexts/LanguageContext";
import {
	Card,
	CardContent,
	CardHeader,
	CardTitle,
} from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "../components/ui/table";
import {
	Tabs,
	TabsContent,
	TabsList,
	TabsTrigger,
} from "../components/ui/tabs";
import {
	Dialog,
	DialogContent,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "../components/ui/dialog";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "../components/ui/select";
import {
	Popover,
	PopoverContent,
	PopoverTrigger,
} from "../components/ui/popover";
import { Calendar } from "../components/ui/calendar";
import { StripeConnectPanel } from "./StripeConnectPanel";

const toCents = (euros) => Math.round((parseFloat(euros) || 0) * 100);
const uuid = () =>
	typeof crypto !== "undefined" && crypto.randomUUID
		? crypto.randomUUID()
		: `${Date.now()}-${Math.random()}`;

function computeRowTotals(rows) {
	let subtotal = 0;
	let vat = 0;
	for (const r of rows) {
		const net = (parseInt(r.quantity, 10) || 0) * toCents(r.unit);
		subtotal += net;
		vat += Math.round((net * (parseFloat(r.vat_rate) || 0)) / 100);
	}
	return { subtotal, vat, total: subtotal + vat };
}

// Common Italian VAT rates. Informational only — this is not a fiscal system
// (no electronic invoicing / SdI): the coach's own fiscal document remains authoritative.
const VAT_RATE_OPTIONS = [0, 4, 5, 10, 22];

// Prevents accidental data loss: outside-click / Escape never close these dialogs.
const NO_DISMISS_PROPS = {
	onPointerDownOutside: (e) => e.preventDefault(),
	onEscapeKeyDown: (e) => e.preventDefault(),
};

function DatePickerField({ value, onChange, placeholder }) {
	const { t, language } = useLanguage();
	const [open, setOpen] = useState(false);
	const locale = language === "it" ? itLocale : enUS;
	const selected = value ? parseLocalDate(value) : undefined;

	return (
		<Popover open={open} onOpenChange={setOpen}>
			<PopoverTrigger asChild>
				<Button
					type="button"
					variant="outline"
					className="w-full justify-start rounded-sm font-normal"
				>
					<CalendarIcon className="w-4 h-4 mr-2 shrink-0 text-muted-foreground" />
					{selected ? formatShortDate(value) : placeholder || t("pickDate")}
				</Button>
			</PopoverTrigger>
			<PopoverContent
				className="w-auto p-0 bg-card border-border"
				align="start"
			>
				<Calendar
					mode="single"
					locale={locale}
					weekStartsOn={1}
					selected={selected}
					onSelect={(date) => {
						if (date) {
							onChange(toLocalISODate(date));
							setOpen(false);
						}
					}}
				/>
			</PopoverContent>
		</Popover>
	);
}

const INVOICE_STATUS_META = {
	draft: { key: "statusDraft", cls: "bg-muted text-muted-foreground" },
	open: { key: "statusOpen", cls: "bg-volt/10 text-volt border-volt/20" },
	partially_paid: {
		key: "statusPartiallyPaid",
		cls: "bg-acid/10 text-acid border-acid/20",
	},
	paid: { key: "statusPaid", cls: "bg-volt/15 text-volt border-volt/30" },
	overdue: {
		key: "statusOverdue",
		cls: "bg-blaze/10 text-blaze border-blaze/20",
	},
	void: { key: "statusVoid", cls: "bg-muted text-muted-foreground" },
	refunded: { key: "statusRefunded", cls: "bg-muted text-muted-foreground" },
};

function StatusBadge({ status }) {
	const { t } = useLanguage();
	const entry = INVOICE_STATUS_META[status];
	const label = entry ? t(entry.key) : status;
	const cls = entry ? entry.cls : "bg-muted";
	return <Badge className={`rounded-sm border ${cls}`}>{label}</Badge>;
}

export function BillingTab() {
	const { t } = useLanguage();
	const [tab, setTab] = useState("overview");
	const [invoices, setInvoices] = useState([]);
	const [payments, setPayments] = useState([]);
	const [products, setProducts] = useState([]);
	const [athletes, setAthletes] = useState([]);
	const [loading, setLoading] = useState(true);

	const load = useCallback(async () => {
		setLoading(true);
		try {
			const [inv, pay, prod, ath] = await Promise.all([
				fetchAPI("/invoices"),
				fetchAPI("/payments"),
				fetchAPI("/products"),
				fetchAPI("/users/athletes"),
			]);
			setInvoices(inv || []);
			setPayments(pay || []);
			setProducts(prod || []);
			setAthletes(ath || []);
		} catch (err) {
			toast.error(err.detail || err.message || "Errore");
		} finally {
			setLoading(false);
		}
	}, []);

	useEffect(() => {
		load();
	}, [load]);

	const athleteName = useCallback(
		(id) => athletes.find((a) => a.user_id === id)?.name || id,
		[athletes],
	);

	const athleteById = useMemo(() => {
		const map = {};
		for (const a of athletes) map[a.user_id] = a;
		return map;
	}, [athletes]);

	const invoiceDate = (inv) =>
		(inv.issued_at || inv.created_at || "").slice(0, 10);

	const kpis = useMemo(() => {
		const collected = payments
			.filter(
				(p) => p.status === "succeeded" || p.status === "partially_refunded",
			)
			.reduce((s, p) => s + (p.amount_cents || 0), 0);
		const toCollect = invoices
			.filter((i) => ["open", "partially_paid", "overdue"].includes(i.status))
			.reduce((s, i) => s + (i.amount_due_cents || 0), 0);
		const overdue = invoices
			.filter((i) => i.status === "overdue")
			.reduce((s, i) => s + (i.amount_due_cents || 0), 0);
		return { collected, toCollect, overdue };
	}, [invoices, payments]);

	// ----- Invoice list: filter by athlete/status/date + sort by date -----
	const [invoiceAthleteFilter, setInvoiceAthleteFilter] = useState("__all__");
	const [invoiceStatusFilter, setInvoiceStatusFilter] = useState("__all__");
	const [invoiceDateFrom, setInvoiceDateFrom] = useState("");
	const [invoiceDateTo, setInvoiceDateTo] = useState("");
	const [invoiceSortDir, setInvoiceSortDir] = useState("desc");

	const invoiceAthleteOptions = useMemo(() => {
		const ids = new Set(invoices.map((i) => i.payer_athlete_id));
		return athletes
			.filter((a) => ids.has(a.user_id))
			.slice()
			.sort((a, b) => (a.name || "").localeCompare(b.name || ""));
	}, [invoices, athletes]);

	const invoiceStatusOptions = useMemo(
		() => [...new Set(invoices.map((i) => i.status))].sort(),
		[invoices],
	);

	const hasInvoiceFilters =
		invoiceAthleteFilter !== "__all__" ||
		invoiceStatusFilter !== "__all__" ||
		invoiceDateFrom ||
		invoiceDateTo;

	const resetInvoiceFilters = () => {
		setInvoiceAthleteFilter("__all__");
		setInvoiceStatusFilter("__all__");
		setInvoiceDateFrom("");
		setInvoiceDateTo("");
	};

	const visibleInvoices = useMemo(() => {
		let list = invoices;
		if (invoiceAthleteFilter !== "__all__") {
			list = list.filter((i) => i.payer_athlete_id === invoiceAthleteFilter);
		}
		if (invoiceStatusFilter !== "__all__") {
			list = list.filter((i) => i.status === invoiceStatusFilter);
		}
		if (invoiceDateFrom) {
			list = list.filter((i) => invoiceDate(i) >= invoiceDateFrom);
		}
		if (invoiceDateTo) {
			list = list.filter((i) => invoiceDate(i) <= invoiceDateTo);
		}
		const sorted = [...list].sort((a, b) => {
			const cmp = invoiceDate(a).localeCompare(invoiceDate(b));
			return invoiceSortDir === "asc" ? cmp : -cmp;
		});
		return sorted;
	}, [
		invoices,
		invoiceAthleteFilter,
		invoiceStatusFilter,
		invoiceDateFrom,
		invoiceDateTo,
		invoiceSortDir,
	]);

	// ----- Payments list: filter by athlete/status -----
	const [paymentAthleteFilter, setPaymentAthleteFilter] = useState("__all__");
	const [paymentStatusFilter, setPaymentStatusFilter] = useState("__all__");

	const invoiceById = useMemo(() => {
		const map = {};
		for (const inv of invoices) map[inv.invoice_id] = inv;
		return map;
	}, [invoices]);

	const paymentAthleteOptions = useMemo(() => {
		const ids = new Set(
			payments
				.map((p) => invoiceById[p.invoice_id]?.payer_athlete_id)
				.filter(Boolean),
		);
		return athletes
			.filter((a) => ids.has(a.user_id))
			.slice()
			.sort((a, b) => (a.name || "").localeCompare(b.name || ""));
	}, [payments, invoiceById, athletes]);

	const paymentStatusOptions = useMemo(
		() => [...new Set(payments.map((p) => p.status))].sort(),
		[payments],
	);

	const hasPaymentFilters =
		paymentAthleteFilter !== "__all__" || paymentStatusFilter !== "__all__";

	const resetPaymentFilters = () => {
		setPaymentAthleteFilter("__all__");
		setPaymentStatusFilter("__all__");
	};

	const visiblePayments = useMemo(() => {
		let list = payments;
		if (paymentAthleteFilter !== "__all__") {
			list = list.filter(
				(p) =>
					invoiceById[p.invoice_id]?.payer_athlete_id === paymentAthleteFilter,
			);
		}
		if (paymentStatusFilter !== "__all__") {
			list = list.filter((p) => p.status === paymentStatusFilter);
		}
		return list;
	}, [payments, invoiceById, paymentAthleteFilter, paymentStatusFilter]);

	// ----- Dialog state -----
	const [invoiceDialog, setInvoiceDialog] = useState(false);
	const [editDialog, setEditDialog] = useState(null); // invoice being edited
	const [payDialog, setPayDialog] = useState(null); // invoice being paid
	const [refundDialog, setRefundDialog] = useState(null); // payment being refunded
	const [productDialog, setProductDialog] = useState(null); // product or {} for new

	return (
		<div className="max-w-7xl mx-auto px-4 md:px-6 py-6 md:py-8 space-y-6">
			<div className="flex items-center justify-between">
				<div>
					<h1 className="font-heading text-3xl flex items-center gap-2">
						<CreditCard className="w-7 h-7 text-volt" />
						{t("billing")}
					</h1>
				</div>
			</div>

			<Tabs value={tab} onValueChange={setTab}>
				<TabsList className="rounded-sm">
					<TabsTrigger value="overview" className="rounded-sm">
						{t("overview")}
					</TabsTrigger>
					<TabsTrigger value="invoices" className="rounded-sm">
						{t("invoices")}
					</TabsTrigger>
					<TabsTrigger value="payments" className="rounded-sm">
						{t("payments")}
					</TabsTrigger>
					<TabsTrigger value="products" className="rounded-sm">
						{t("products")}
					</TabsTrigger>
					<TabsTrigger value="online" className="rounded-sm">
						{t("onlinePayments")}
					</TabsTrigger>
				</TabsList>

				{/* Overview */}
				<TabsContent value="overview" className="space-y-4">
					<div className="grid grid-cols-1 md:grid-cols-3 gap-4">
						<KpiCard
							icon={Euro}
							label={t("collected")}
							value={formatMoney(kpis.collected)}
							accent="text-volt"
						/>
						<KpiCard
							icon={Wallet}
							label={t("toCollect")}
							value={formatMoney(kpis.toCollect)}
						/>
						<KpiCard
							icon={Clock}
							label={t("overdueAmount")}
							value={formatMoney(kpis.overdue)}
							accent="text-blaze"
						/>
					</div>
					<Card className="bg-card border-border rounded-sm">
						<CardHeader>
							<CardTitle className="text-base">{t("recentPayments")}</CardTitle>
						</CardHeader>
						<CardContent>
							{payments.length === 0 ? (
								<p className="text-sm text-muted-foreground">
									{t("noPayments")}
								</p>
							) : (
								<div className="space-y-2">
									{payments.slice(0, 6).map((p) => (
										<div
											key={p.payment_id}
											className="flex items-center justify-between text-sm border-b border-border/50 pb-2"
										>
											<span className="text-muted-foreground">
												{p.paid_at ? formatShortDate(p.paid_at) : "-"} ·{" "}
												{t(`method${methodKey(p.method)}`)}
											</span>
											<span className="font-mono">
												{formatMoney(p.amount_cents)}
											</span>
										</div>
									))}
								</div>
							)}
						</CardContent>
					</Card>
				</TabsContent>

				{/* Invoices */}
				<TabsContent value="invoices" className="space-y-4">
					<div className="flex flex-col md:flex-row md:items-end gap-3 justify-between">
						<div className="flex flex-col sm:flex-row flex-wrap gap-2 sm:items-center">
							<AthleteFilterCombo
								athletes={invoiceAthleteOptions}
								value={invoiceAthleteFilter}
								onChange={setInvoiceAthleteFilter}
								allLabel={t("allAthletes")}
							/>
							<Select
								value={invoiceStatusFilter}
								onValueChange={setInvoiceStatusFilter}
							>
								<SelectTrigger className="rounded-sm sm:w-[170px]">
									<SelectValue placeholder={t("filterByStatus")} />
								</SelectTrigger>
								<SelectContent>
									<SelectItem value="__all__">{t("allStatuses")}</SelectItem>
									{invoiceStatusOptions.map((status) => (
										<SelectItem key={status} value={status}>
											{t(INVOICE_STATUS_META[status]?.key || status)}
										</SelectItem>
									))}
								</SelectContent>
							</Select>
							<div className="flex items-center gap-2">
								<div className="w-full sm:w-[160px]">
									<DatePickerField
										value={invoiceDateFrom}
										onChange={setInvoiceDateFrom}
										placeholder={t("dateFrom")}
									/>
								</div>
								<div className="w-full sm:w-[160px]">
									<DatePickerField
										value={invoiceDateTo}
										onChange={setInvoiceDateTo}
										placeholder={t("dateTo")}
									/>
								</div>
							</div>
							{hasInvoiceFilters && (
								<Button
									variant="ghost"
									size="sm"
									className="rounded-sm text-muted-foreground"
									onClick={resetInvoiceFilters}
								>
									<FilterX className="w-3.5 h-3.5 mr-1" />
									{t("resetFilters")}
								</Button>
							)}
						</div>
						<Button
							className="bg-volt hover:bg-volt/90 rounded-sm shrink-0"
							onClick={() => setInvoiceDialog(true)}
						>
							<Plus className="w-4 h-4 mr-2" />
							{t("createInvoice")}
						</Button>
					</div>
					<InvoiceTable
						invoices={visibleInvoices}
						athleteById={athleteById}
						loading={loading}
						sortDir={invoiceSortDir}
						onToggleSort={() =>
							setInvoiceSortDir((d) => (d === "asc" ? "desc" : "asc"))
						}
						onIssue={async (inv) => {
							try {
								await fetchAPI(`/invoices/${inv.invoice_id}/issue`, {
									method: "POST",
								});
								toast.success(t("invoiceIssued"));
								load();
							} catch (err) {
								toast.error(err.detail || err.message);
							}
						}}
						onVoid={async (inv) => {
							if (!window.confirm(t("confirmVoidInvoice"))) return;
							try {
								await fetchAPI(`/invoices/${inv.invoice_id}/void`, {
									method: "POST",
								});
								toast.success(t("invoiceVoided"));
								load();
							} catch (err) {
								toast.error(err.detail || err.message);
							}
						}}
						onPay={(inv) => setPayDialog(inv)}
						onEdit={(inv) => setEditDialog(inv)}
					/>
				</TabsContent>

				{/* Payments */}
				<TabsContent value="payments" className="space-y-4">
					<div className="flex flex-col sm:flex-row flex-wrap gap-2 sm:items-center">
						<AthleteFilterCombo
							athletes={paymentAthleteOptions}
							value={paymentAthleteFilter}
							onChange={setPaymentAthleteFilter}
							allLabel={t("allAthletes")}
						/>
						<Select
							value={paymentStatusFilter}
							onValueChange={setPaymentStatusFilter}
						>
							<SelectTrigger className="rounded-sm sm:w-[170px]">
								<SelectValue placeholder={t("filterByStatus")} />
							</SelectTrigger>
							<SelectContent>
								<SelectItem value="__all__">{t("allStatuses")}</SelectItem>
								{paymentStatusOptions.map((status) => (
									<SelectItem key={status} value={status}>
										{t(PAYMENT_STATUS_META[status]?.key || status)}
									</SelectItem>
								))}
							</SelectContent>
						</Select>
						{hasPaymentFilters && (
							<Button
								variant="ghost"
								size="sm"
								className="rounded-sm text-muted-foreground"
								onClick={resetPaymentFilters}
							>
								<FilterX className="w-3.5 h-3.5 mr-1" />
								{t("resetFilters")}
							</Button>
						)}
					</div>
					<PaymentTable
						payments={visiblePayments}
						invoiceById={invoiceById}
						athleteById={athleteById}
						loading={loading}
						onRefund={(p) => setRefundDialog(p)}
					/>
				</TabsContent>

				{/* Products */}
				<TabsContent value="products" className="space-y-4">
					<div className="flex justify-end">
						<Button
							className="bg-volt hover:bg-volt/90 rounded-sm"
							onClick={() => setProductDialog({})}
						>
							<Plus className="w-4 h-4 mr-2" />
							{t("newProduct")}
						</Button>
					</div>
					<ProductTable
						products={products}
						loading={loading}
						onEdit={(p) => setProductDialog(p)}
						onDisable={async (p) => {
							try {
								await fetchAPI(`/products/${p.product_id}`, {
									method: "DELETE",
								});
								load();
							} catch (err) {
								toast.error(err.detail || err.message);
							}
						}}
					/>
				</TabsContent>

				{/* Online payments (Stripe Connect) */}
				<TabsContent value="online" className="space-y-4">
					<StripeConnectPanel />
				</TabsContent>
			</Tabs>

			{invoiceDialog && (
				<CreateInvoiceDialog
					athletes={athletes}
					products={products}
					onClose={() => setInvoiceDialog(false)}
					onCreated={() => {
						setInvoiceDialog(false);
						load();
					}}
				/>
			)}
			{payDialog && (
				<RecordPaymentDialog
					invoice={payDialog}
					onClose={() => setPayDialog(null)}
					onDone={() => {
						setPayDialog(null);
						load();
					}}
				/>
			)}
			{editDialog && (
				<EditInvoiceDialog
					invoice={editDialog}
					athleteName={athleteName}
					products={products}
					onClose={() => setEditDialog(null)}
					onSaved={() => {
						setEditDialog(null);
						load();
					}}
				/>
			)}
			{refundDialog && (
				<RefundDialog
					payment={refundDialog}
					onClose={() => setRefundDialog(null)}
					onDone={() => {
						setRefundDialog(null);
						load();
					}}
				/>
			)}
			{productDialog && (
				<ProductDialog
					product={productDialog}
					onClose={() => setProductDialog(null)}
					onSaved={() => {
						setProductDialog(null);
						load();
					}}
				/>
			)}
		</div>
	);
}

function methodKey(method) {
	const map = {
		cash: "Cash",
		bank_transfer: "BankTransfer",
		pos: "Pos",
		other: "Other",
	};
	return map[method] || "Other";
}

function KpiCard({ icon: Icon, label, value, accent }) {
	return (
		<Card className="bg-card border-border rounded-sm">
			<CardContent className="pt-6">
				<p className="label-tactical text-muted-foreground flex items-center gap-2">
					<Icon className={`w-4 h-4 ${accent || ""}`} />
					{label}
				</p>
				<p className={`font-heading text-3xl mt-2 ${accent || ""}`}>{value}</p>
			</CardContent>
		</Card>
	);
}

function InvoiceTable({
	invoices,
	athleteById,
	loading,
	sortDir,
	onToggleSort,
	onIssue,
	onVoid,
	onPay,
	onEdit,
}) {
	const { t } = useLanguage();
	if (loading)
		return <p className="text-sm text-muted-foreground">{t("loading")}</p>;
	if (invoices.length === 0)
		return <p className="text-sm text-muted-foreground">{t("noInvoices")}</p>;
	return (
		<div className="border border-border rounded-sm overflow-x-auto">
			<Table>
				<TableHeader>
					<TableRow>
						<TableHead>{t("invoice")}</TableHead>
						<TableHead>{t("payer")}</TableHead>
						<TableHead>
							<button
								type="button"
								onClick={onToggleSort}
								className="flex items-center gap-1 hover:text-foreground transition-colors"
							>
								{t("invoiceDate")}
								{sortDir === "asc" ? (
									<ArrowUp className="w-3 h-3" />
								) : (
									<ArrowDown className="w-3 h-3" />
								)}
							</button>
						</TableHead>
						<TableHead>{t("dueDate")}</TableHead>
						<TableHead>{t("total")}</TableHead>
						<TableHead>{t("amountDue")}</TableHead>
						<TableHead>{t("paymentStatus")}</TableHead>
						<TableHead className="text-right">{t("paymentActions")}</TableHead>
					</TableRow>
				</TableHeader>
				<TableBody>
					{invoices.map((inv) => (
						<TableRow key={inv.invoice_id}>
							<TableCell className="font-mono text-xs">
								{inv.number || inv.invoice_id.slice(0, 10)}
							</TableCell>
							<TableCell>
								<AthleteCell athlete={athleteById[inv.payer_athlete_id]} />
							</TableCell>
							<TableCell className="text-sm text-muted-foreground">
								{formatShortDate(inv.issued_at || inv.created_at)}
							</TableCell>
							<TableCell className="text-sm text-muted-foreground">
								{inv.due_date ? formatShortDate(inv.due_date) : "-"}
							</TableCell>
							<TableCell className="font-mono">
								{formatMoney(inv.total_cents)}
							</TableCell>
							<TableCell className="font-mono">
								{formatMoney(inv.amount_due_cents)}
							</TableCell>
							<TableCell>
								<StatusBadge status={inv.status} />
							</TableCell>
							<TableCell className="text-right space-x-1 whitespace-nowrap">
								{inv.status === "draft" && (
									<Button
										size="sm"
										variant="outline"
										className="rounded-sm"
										onClick={() => onIssue(inv)}
									>
										<Send className="w-3.5 h-3.5 mr-1" />
										{t("issueInvoice")}
									</Button>
								)}
								{["open", "partially_paid", "overdue"].includes(inv.status) && (
									<Button
										size="sm"
										className="bg-volt hover:bg-volt/90 rounded-sm"
										onClick={() => onPay(inv)}
									>
										<Receipt className="w-3.5 h-3.5 mr-1" />
										{t("recordPayment")}
									</Button>
								)}
								{!["void", "refunded"].includes(inv.status) && (
									<Button
										size="sm"
										variant="outline"
										className="rounded-sm"
										onClick={() => onEdit(inv)}
									>
										<Pencil className="w-3.5 h-3.5" />
									</Button>
								)}
								{["draft", "open", "overdue", "partially_paid"].includes(
									inv.status,
								) && (
									<Button
										size="sm"
										variant="outline"
										className="rounded-sm text-blaze border-blaze/30"
										onClick={() => onVoid(inv)}
									>
										<Ban className="w-3.5 h-3.5" />
									</Button>
								)}
							</TableCell>
						</TableRow>
					))}
				</TableBody>
			</Table>
		</div>
	);
}

const PAYMENT_STATUS_META = {
	pending: {
		key: "paymentStatusPending",
		cls: "bg-muted text-muted-foreground",
	},
	succeeded: {
		key: "paymentStatusSucceeded",
		cls: "bg-volt/10 text-volt border-volt/20",
	},
	failed: {
		key: "paymentStatusFailed",
		cls: "bg-blaze/10 text-blaze border-blaze/20",
	},
	canceled: {
		key: "paymentStatusCanceled",
		cls: "bg-muted text-muted-foreground",
	},
	refunded: {
		key: "paymentStatusRefunded",
		cls: "bg-muted text-muted-foreground",
	},
	partially_refunded: {
		key: "paymentStatusPartiallyRefunded",
		cls: "bg-acid/10 text-acid border-acid/20",
	},
};

function PaymentStatusBadge({ status }) {
	const { t } = useLanguage();
	const entry = PAYMENT_STATUS_META[status];
	const label = entry ? t(entry.key) : status;
	const cls = entry ? entry.cls : "bg-muted";
	return <Badge className={`rounded-sm border ${cls}`}>{label}</Badge>;
}

function PaymentTable({
	payments,
	invoiceById,
	athleteById,
	loading,
	onRefund,
}) {
	const { t } = useLanguage();

	if (loading)
		return <p className="text-sm text-muted-foreground">{t("loading")}</p>;
	if (payments.length === 0)
		return <p className="text-sm text-muted-foreground">{t("noPayments")}</p>;
	return (
		<div className="border border-border rounded-sm overflow-x-auto">
			<Table>
				<TableHeader>
					<TableRow>
						<TableHead>{t("paymentDate")}</TableHead>
						<TableHead>{t("invoice")}</TableHead>
						<TableHead>{t("payer")}</TableHead>
						<TableHead>{t("paymentMethod")}</TableHead>
						<TableHead>{t("amount")}</TableHead>
						<TableHead>{t("paymentStatus")}</TableHead>
						<TableHead className="text-right">{t("paymentActions")}</TableHead>
					</TableRow>
				</TableHeader>
				<TableBody>
					{payments.map((p) => {
						const inv = invoiceById[p.invoice_id];
						return (
							<TableRow key={p.payment_id}>
								<TableCell className="text-sm text-muted-foreground">
									{p.paid_at ? formatShortDate(p.paid_at) : "-"}
								</TableCell>
								<TableCell className="font-mono text-xs">
									{inv?.number || p.invoice_id.slice(0, 10)}
								</TableCell>
								<TableCell>
									{inv ? (
										<AthleteCell athlete={athleteById[inv.payer_athlete_id]} />
									) : (
										"-"
									)}
								</TableCell>
								<TableCell>{t(`method${methodKey(p.method)}`)}</TableCell>
								<TableCell className="font-mono">
									{formatMoney(p.amount_cents)}
								</TableCell>
								<TableCell>
									<PaymentStatusBadge status={p.status} />
								</TableCell>
								<TableCell className="text-right whitespace-nowrap">
									{["succeeded", "partially_refunded"].includes(p.status) && (
										<Button
											size="sm"
											variant="outline"
											className="rounded-sm text-blaze border-blaze/30"
											onClick={() => onRefund(p)}
										>
											<RotateCcw className="w-3.5 h-3.5 mr-1" />
											{t("refund")}
										</Button>
									)}
								</TableCell>
							</TableRow>
						);
					})}
				</TableBody>
			</Table>
		</div>
	);
}

function ProductTable({ products, loading, onEdit, onDisable }) {
	const { t } = useLanguage();
	if (loading)
		return <p className="text-sm text-muted-foreground">{t("loading")}</p>;
	if (products.length === 0)
		return <p className="text-sm text-muted-foreground">{t("noProducts")}</p>;
	return (
		<div className="border border-border rounded-sm overflow-x-auto">
			<Table>
				<TableHeader>
					<TableRow>
						<TableHead>{t("name")}</TableHead>
						<TableHead>{t("unitPrice")}</TableHead>
						<TableHead className="text-right">{t("paymentActions")}</TableHead>
					</TableRow>
				</TableHeader>
				<TableBody>
					{products.map((p) => (
						<TableRow
							key={p.product_id}
							className={p.is_active ? "" : "opacity-50"}
						>
							<TableCell>{p.name}</TableCell>
							<TableCell className="font-mono">
								{formatMoney(p.unit_amount_cents, p.currency)}
							</TableCell>
							<TableCell className="text-right space-x-1 whitespace-nowrap">
								<Button
									size="sm"
									variant="outline"
									className="rounded-sm"
									onClick={() => onEdit(p)}
								>
									{t("edit")}
								</Button>
								{p.is_active && (
									<Button
										size="sm"
										variant="outline"
										className="rounded-sm text-blaze border-blaze/30"
										onClick={() => onDisable(p)}
									>
										<Trash2 className="w-3.5 h-3.5" />
									</Button>
								)}
							</TableCell>
						</TableRow>
					))}
				</TableBody>
			</Table>
		</div>
	);
}

function AthleteCell({ athlete }) {
	if (!athlete) return <span className="text-muted-foreground">-</span>;
	return (
		<div className="min-w-0">
			<p className="text-sm font-medium truncate">{athlete.name}</p>
			<p className="text-xs text-muted-foreground truncate">
				{athlete.email || "-"}
			</p>
		</div>
	);
}

function AthleteFilterCombo({ athletes, value, onChange, allLabel }) {
	const { t } = useLanguage();
	const [open, setOpen] = useState(false);
	const [query, setQuery] = useState("");
	const selected = athletes.find((a) => a.user_id === value);

	const filtered = useMemo(() => {
		const q = query.trim().toLowerCase();
		if (!q) return athletes;
		return athletes.filter(
			(a) =>
				(a.name || "").toLowerCase().includes(q) ||
				(a.email || "").toLowerCase().includes(q),
		);
	}, [athletes, query]);

	return (
		<Popover
			open={open}
			onOpenChange={(o) => {
				setOpen(o);
				if (!o) setQuery("");
			}}
		>
			<PopoverTrigger asChild>
				<Button
					type="button"
					variant="outline"
					className="rounded-sm justify-start font-normal sm:w-[220px]"
				>
					<Search className="w-3.5 h-3.5 mr-2 shrink-0 text-muted-foreground" />
					<span className="truncate">
						{selected ? selected.name : allLabel}
					</span>
				</Button>
			</PopoverTrigger>
			<PopoverContent className="w-64 p-0 bg-card border-border" align="start">
				<div className="p-2 border-b border-border">
					<div className="relative">
						<Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
						<Input
							className="pl-8 rounded-sm h-8"
							placeholder={t("searchAthlete")}
							value={query}
							onChange={(e) => setQuery(e.target.value)}
							autoFocus
						/>
					</div>
				</div>
				<div className="max-h-64 overflow-y-auto">
					<button
						type="button"
						onClick={() => {
							onChange("__all__");
							setOpen(false);
						}}
						className={`w-full text-left px-3 py-2 text-sm hover:bg-white/5 transition-colors ${
							value === "__all__" ? "text-volt" : ""
						}`}
					>
						{allLabel}
					</button>
					<div className="divide-y divide-border/60 border-t border-border/60">
						{filtered.length === 0 ? (
							<p className="text-sm text-muted-foreground text-center py-4">
								{t("noAthleteFound")}
							</p>
						) : (
							filtered.map((a) => (
								<button
									type="button"
									key={a.user_id}
									onClick={() => {
										onChange(a.user_id);
										setOpen(false);
									}}
									className={`w-full text-left px-3 py-2 hover:bg-white/5 transition-colors ${
										value === a.user_id ? "bg-white/5" : ""
									}`}
								>
									<p className="text-sm font-medium truncate">{a.name}</p>
									<p className="text-xs text-muted-foreground truncate">
										{a.email || "-"}
									</p>
								</button>
							))
						)}
					</div>
				</div>
			</PopoverContent>
		</Popover>
	);
}

function AthletePicker({ athletes, value, onChange }) {
	const { t } = useLanguage();
	const [query, setQuery] = useState("");
	const selected = athletes.find((a) => a.user_id === value);

	const filtered = useMemo(() => {
		const q = query.trim().toLowerCase();
		if (!q) return athletes;
		return athletes.filter(
			(a) =>
				(a.name || "").toLowerCase().includes(q) ||
				(a.email || "").toLowerCase().includes(q),
		);
	}, [athletes, query]);

	if (selected) {
		return (
			<div className="flex items-center justify-between gap-3 rounded-sm border border-border bg-secondary px-3 py-2">
				<div className="min-w-0">
					<p className="text-sm font-medium truncate">{selected.name}</p>
					<p className="text-xs text-muted-foreground truncate">
						{selected.email || "-"}
					</p>
				</div>
				<Button
					type="button"
					variant="ghost"
					size="sm"
					className="rounded-sm shrink-0"
					onClick={() => onChange("")}
				>
					<X className="w-4 h-4" />
				</Button>
			</div>
		);
	}

	return (
		<div className="space-y-2">
			<div className="relative">
				<Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
				<Input
					className="pl-8 rounded-sm"
					placeholder={t("searchAthlete")}
					value={query}
					onChange={(e) => setQuery(e.target.value)}
				/>
			</div>
			<div className="rounded-sm border border-border max-h-56 overflow-y-auto">
				{filtered.length === 0 ? (
					<p className="text-sm text-muted-foreground text-center py-4">
						{t("noAthleteFound")}
					</p>
				) : (
					<div className="divide-y divide-border/60">
						{filtered.map((a) => (
							<button
								type="button"
								key={a.user_id}
								onClick={() => onChange(a.user_id)}
								className="w-full text-left px-3 py-2 hover:bg-white/5 transition-colors"
							>
								<p className="text-sm font-medium truncate">{a.name}</p>
								<p className="text-xs text-muted-foreground truncate">
									{a.email || "-"}
								</p>
							</button>
						))}
					</div>
				)}
			</div>
		</div>
	);
}

function LineItemRows({ rows, setRows, products, editable = true }) {
	const { t } = useLanguage();

	const setRow = (i, patch) =>
		setRows((r) =>
			r.map((row, idx) => (idx === i ? { ...row, ...patch } : row)),
		);

	const applyProduct = (i, productId) => {
		if (!productId) {
			setRow(i, { product_id: "", description: "" });
			return;
		}
		const product = products.find((p) => p.product_id === productId);
		if (!product) return;
		setRow(i, {
			product_id: productId,
			description: product.name,
			unit: (product.unit_amount_cents / 100).toFixed(2),
		});
	};

	if (!editable) {
		return (
			<div className="space-y-1.5">
				{rows.map((row, i) => {
					const net = (parseInt(row.quantity, 10) || 0) * toCents(row.unit);
					const vat = Math.round((net * (parseFloat(row.vat_rate) || 0)) / 100);
					return (
						<div
							key={row.line_id || i}
							className="flex items-center justify-between text-sm border-b border-border/50 pb-1.5"
						>
							<span className="text-muted-foreground">
								{parseInt(row.quantity, 10) > 1 ? `${row.quantity}× ` : ""}
								{row.description}
								{parseFloat(row.vat_rate) > 0 && (
									<span className="text-xs"> (IVA {row.vat_rate}%)</span>
								)}
							</span>
							<span className="font-mono">{formatMoney(net + vat)}</span>
						</div>
					);
				})}
			</div>
		);
	}

	return (
		<div className="space-y-2">
			{rows.map((row, i) => {
				const isCustom = !row.product_id;
				const net = (parseInt(row.quantity, 10) || 0) * toCents(row.unit);
				const vat = Math.round((net * (parseFloat(row.vat_rate) || 0)) / 100);
				return (
					<div
						key={i}
						className="space-y-2 rounded-sm border border-border/60 p-3"
					>
						<div className="flex items-center gap-2">
							<Select
								value={row.product_id || "__custom__"}
								onValueChange={(v) =>
									applyProduct(i, v === "__custom__" ? "" : v)
								}
							>
								<SelectTrigger className="flex-1 rounded-sm">
									<SelectValue placeholder={t("selectProduct")} />
								</SelectTrigger>
								<SelectContent>
									<SelectItem value="__custom__">
										{t("customLineItem")}
									</SelectItem>
									{products
										.filter((p) => p.is_active)
										.map((p) => (
											<SelectItem key={p.product_id} value={p.product_id}>
												{p.name} ·{" "}
												{formatMoney(p.unit_amount_cents, p.currency)}
											</SelectItem>
										))}
								</SelectContent>
							</Select>
							<Button
								variant="ghost"
								size="sm"
								className="rounded-sm text-blaze shrink-0"
								onClick={() => setRows((r) => r.filter((_, idx) => idx !== i))}
							>
								<Trash2 className="w-4 h-4" />
							</Button>
						</div>

						{isCustom && (
							<Input
								className="rounded-sm"
								placeholder={t("lineDescription")}
								value={row.description}
								onChange={(e) => setRow(i, { description: e.target.value })}
							/>
						)}

						<div className="grid grid-cols-2 sm:grid-cols-4 gap-2 items-center">
							<Input
								type="number"
								min="1"
								className="rounded-sm"
								placeholder={t("quantity")}
								value={row.quantity}
								onChange={(e) => setRow(i, { quantity: e.target.value })}
							/>
							<Input
								type="number"
								min="0"
								step="0.01"
								className="rounded-sm"
								placeholder={`${t("unitPrice")} (€)`}
								value={row.unit}
								onChange={(e) => setRow(i, { unit: e.target.value })}
							/>
							<Select
								value={String(row.vat_rate ?? 0)}
								onValueChange={(v) => setRow(i, { vat_rate: parseFloat(v) })}
							>
								<SelectTrigger className="rounded-sm">
									<SelectValue />
								</SelectTrigger>
								<SelectContent>
									{VAT_RATE_OPTIONS.map((rate) => (
										<SelectItem key={rate} value={String(rate)}>
											{t("vat")} {rate}%
										</SelectItem>
									))}
								</SelectContent>
							</Select>
							<div className="text-right font-mono text-sm self-center">
								{formatMoney(net + vat)}
							</div>
						</div>
					</div>
				);
			})}
			<Button
				variant="outline"
				size="sm"
				className="rounded-sm"
				onClick={() =>
					setRows((r) => [
						...r,
						{
							description: "",
							quantity: 1,
							unit: "",
							product_id: "",
							vat_rate: 0,
						},
					])
				}
			>
				<Plus className="w-4 h-4 mr-1" />
				{t("addLineItem")}
			</Button>
		</div>
	);
}

function CreateInvoiceDialog({ athletes, products, onClose, onCreated }) {
	const { t } = useLanguage();
	const [payer, setPayer] = useState("");
	const [dueDate, setDueDate] = useState("");
	const [notes, setNotes] = useState("");
	const [rows, setRows] = useState([
		{ description: "", quantity: 1, unit: "", product_id: "", vat_rate: 0 },
	]);
	const [saving, setSaving] = useState(false);

	const totals = computeRowTotals(rows);

	const submit = async () => {
		if (!payer) {
			toast.error(t("selectAthlete"));
			return;
		}
		const line_items = rows
			.filter((r) => r.description && r.unit)
			.map((r) => ({
				product_id: r.product_id || null,
				description: r.description,
				quantity: parseInt(r.quantity, 10) || 1,
				unit_amount_cents: toCents(r.unit),
				vat_rate: parseFloat(r.vat_rate) || 0,
			}));
		if (line_items.length === 0) {
			toast.error(t("addLineItem"));
			return;
		}
		setSaving(true);
		try {
			await fetchAPI("/invoices", {
				method: "POST",
				body: JSON.stringify({
					payer_athlete_id: payer,
					line_items,
					due_date: dueDate || null,
					notes: notes || null,
				}),
			});
			toast.success(t("invoiceCreated"));
			onCreated();
		} catch (err) {
			toast.error(err.detail || err.message);
		} finally {
			setSaving(false);
		}
	};

	return (
		<Dialog open onOpenChange={(o) => !o && onClose()}>
			<DialogContent className="max-w-2xl rounded-sm" {...NO_DISMISS_PROPS}>
				<DialogHeader>
					<DialogTitle className="font-heading">
						{t("createInvoice")}
					</DialogTitle>
				</DialogHeader>
				<div className="space-y-4">
					<div>
						<Label>{t("payer")}</Label>
						<AthletePicker
							athletes={athletes}
							value={payer}
							onChange={setPayer}
						/>
					</div>

					<div className="space-y-2">
						<Label>{t("lineItems")}</Label>
						<LineItemRows rows={rows} setRows={setRows} products={products} />
						<p className="text-xs text-muted-foreground">
							{t("vatNotFiscalHint")}
						</p>
					</div>

					<div className="grid grid-cols-2 gap-3">
						<div>
							<Label>{t("dueDate")}</Label>
							<DatePickerField value={dueDate} onChange={setDueDate} />
						</div>
						<div>
							<Label>{t("billingNotes")}</Label>
							<Input
								className="rounded-sm"
								value={notes}
								onChange={(e) => setNotes(e.target.value)}
							/>
						</div>
					</div>

					<div className="space-y-1 border-t border-border pt-3">
						<div className="flex justify-between items-center text-sm text-muted-foreground">
							<span>{t("subtotal")}</span>
							<span className="font-mono">{formatMoney(totals.subtotal)}</span>
						</div>
						<div className="flex justify-between items-center text-sm text-muted-foreground">
							<span>{t("vat")}</span>
							<span className="font-mono">{formatMoney(totals.vat)}</span>
						</div>
						<div className="flex justify-between items-center">
							<span className="label-tactical text-muted-foreground">
								{t("total")}
							</span>
							<span className="font-heading text-2xl text-volt">
								{formatMoney(totals.total)}
							</span>
						</div>
					</div>
				</div>
				<DialogFooter>
					<Button variant="outline" className="rounded-sm" onClick={onClose}>
						{t("cancel")}
					</Button>
					<Button
						className="bg-volt hover:bg-volt/90 rounded-sm"
						disabled={saving}
						onClick={submit}
					>
						{t("create")}
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
}

function EditInvoiceDialog({
	invoice,
	athleteName,
	products,
	onClose,
	onSaved,
}) {
	const { t } = useLanguage();
	const linesEditable =
		["draft", "open", "overdue"].includes(invoice.status) &&
		(invoice.amount_paid_cents || 0) === 0;
	const [dueDate, setDueDate] = useState(invoice.due_date || "");
	const [notes, setNotes] = useState(invoice.notes || "");
	const [rows, setRows] = useState(
		(invoice.line_items || []).map((li) => ({
			line_id: li.line_id,
			product_id: li.product_id || "",
			description: li.description,
			quantity: li.quantity,
			unit: (li.unit_amount_cents / 100).toFixed(2),
			vat_rate: li.vat_rate ?? 0,
		})),
	);
	const [saving, setSaving] = useState(false);

	const totals = computeRowTotals(rows);

	const submit = async () => {
		const payload = {
			due_date: dueDate || null,
			notes: notes || null,
		};
		if (linesEditable) {
			const line_items = rows
				.filter((r) => r.description && r.unit)
				.map((r) => ({
					product_id: r.product_id || null,
					description: r.description,
					quantity: parseInt(r.quantity, 10) || 1,
					unit_amount_cents: toCents(r.unit),
					vat_rate: parseFloat(r.vat_rate) || 0,
				}));
			if (line_items.length === 0) {
				toast.error(t("addLineItem"));
				return;
			}
			payload.line_items = line_items;
		}
		setSaving(true);
		try {
			await fetchAPI(`/invoices/${invoice.invoice_id}`, {
				method: "PATCH",
				body: JSON.stringify(payload),
			});
			toast.success(t("invoiceUpdated"));
			onSaved();
		} catch (err) {
			toast.error(err.detail || err.message);
		} finally {
			setSaving(false);
		}
	};

	return (
		<Dialog open onOpenChange={(o) => !o && onClose()}>
			<DialogContent className="max-w-2xl rounded-sm" {...NO_DISMISS_PROPS}>
				<DialogHeader>
					<DialogTitle className="font-heading">{t("editInvoice")}</DialogTitle>
				</DialogHeader>
				<div className="space-y-4">
					<div>
						<Label>{t("payer")}</Label>
						<div className="rounded-sm border border-border bg-secondary px-3 py-2">
							<p className="text-sm font-medium">
								{athleteName(invoice.payer_athlete_id)}
							</p>
						</div>
					</div>

					<div className="space-y-2">
						<Label>{t("lineItems")}</Label>
						{!linesEditable && (
							<p className="text-xs text-muted-foreground">
								{t("lineItemsLockedHint")}
							</p>
						)}
						<LineItemRows
							rows={rows}
							setRows={setRows}
							products={products}
							editable={linesEditable}
						/>
						{linesEditable && (
							<p className="text-xs text-muted-foreground">
								{t("vatNotFiscalHint")}
							</p>
						)}
					</div>

					<div className="grid grid-cols-2 gap-3">
						<div>
							<Label>{t("dueDate")}</Label>
							<DatePickerField value={dueDate} onChange={setDueDate} />
						</div>
						<div>
							<Label>{t("billingNotes")}</Label>
							<Input
								className="rounded-sm"
								value={notes || ""}
								onChange={(e) => setNotes(e.target.value)}
							/>
						</div>
					</div>

					<div className="space-y-1 border-t border-border pt-3">
						<div className="flex justify-between items-center text-sm text-muted-foreground">
							<span>{t("subtotal")}</span>
							<span className="font-mono">{formatMoney(totals.subtotal)}</span>
						</div>
						<div className="flex justify-between items-center text-sm text-muted-foreground">
							<span>{t("vat")}</span>
							<span className="font-mono">{formatMoney(totals.vat)}</span>
						</div>
						<div className="flex justify-between items-center">
							<span className="label-tactical text-muted-foreground">
								{t("total")}
							</span>
							<span className="font-heading text-2xl text-volt">
								{formatMoney(totals.total)}
							</span>
						</div>
					</div>
				</div>
				<DialogFooter>
					<Button variant="outline" className="rounded-sm" onClick={onClose}>
						{t("cancel")}
					</Button>
					<Button
						className="bg-volt hover:bg-volt/90 rounded-sm"
						disabled={saving}
						onClick={submit}
					>
						{t("save")}
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
}

function RefundDialog({ payment, onClose, onDone }) {
	const { t } = useLanguage();
	const [amount, setAmount] = useState(
		((payment.amount_cents || 0) / 100).toFixed(2),
	);
	const [reason, setReason] = useState("");
	const [saving, setSaving] = useState(false);

	const submit = async () => {
		const amount_cents = toCents(amount);
		if (amount_cents <= 0) {
			toast.error(t("refundAmount"));
			return;
		}
		setSaving(true);
		try {
			await fetchAPI(`/payments/${payment.payment_id}/refund`, {
				method: "POST",
				body: JSON.stringify({ amount_cents, reason: reason || null }),
			});
			toast.success(t("refundSaved"));
			onDone();
		} catch (err) {
			toast.error(err.detail || err.message);
		} finally {
			setSaving(false);
		}
	};

	return (
		<Dialog open onOpenChange={(o) => !o && onClose()}>
			<DialogContent className="max-w-md rounded-sm" {...NO_DISMISS_PROPS}>
				<DialogHeader>
					<DialogTitle className="font-heading">{t("refund")}</DialogTitle>
				</DialogHeader>
				<div className="space-y-4">
					<p className="text-sm text-muted-foreground">
						{t("refundHint")} {formatMoney(payment.amount_cents)}
					</p>
					<div>
						<Label>{`${t("refundAmount")} (€)`}</Label>
						<Input
							type="number"
							min="0"
							step="0.01"
							className="rounded-sm"
							value={amount}
							onChange={(e) => setAmount(e.target.value)}
						/>
					</div>
					<div>
						<Label>{t("billingNotes")}</Label>
						<Input
							className="rounded-sm"
							value={reason}
							onChange={(e) => setReason(e.target.value)}
						/>
					</div>
				</div>
				<DialogFooter>
					<Button variant="outline" className="rounded-sm" onClick={onClose}>
						{t("cancel")}
					</Button>
					<Button
						className="bg-blaze hover:bg-blaze/90 rounded-sm"
						disabled={saving}
						onClick={submit}
					>
						{t("refund")}
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
}

function RecordPaymentDialog({ invoice, onClose, onDone }) {
	const { t } = useLanguage();
	const [method, setMethod] = useState("bank_transfer");
	const [amount, setAmount] = useState(
		((invoice.amount_due_cents || 0) / 100).toFixed(2),
	);
	const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
	const [saving, setSaving] = useState(false);

	const submit = async () => {
		setSaving(true);
		try {
			await fetchAPI(`/invoices/${invoice.invoice_id}/payments`, {
				method: "POST",
				headers: { "Idempotency-Key": uuid() },
				body: JSON.stringify({
					method,
					amount_cents: toCents(amount),
					paid_at: date,
				}),
			});
			toast.success(t("paymentSaved"));
			onDone();
		} catch (err) {
			toast.error(err.detail || err.message);
		} finally {
			setSaving(false);
		}
	};

	return (
		<Dialog open onOpenChange={(o) => !o && onClose()}>
			<DialogContent className="max-w-md rounded-sm" {...NO_DISMISS_PROPS}>
				<DialogHeader>
					<DialogTitle className="font-heading">
						{t("recordPayment")}
					</DialogTitle>
				</DialogHeader>
				<div className="space-y-4">
					<div>
						<Label>{t("paymentMethod")}</Label>
						<Select value={method} onValueChange={setMethod}>
							<SelectTrigger className="rounded-sm">
								<SelectValue />
							</SelectTrigger>
							<SelectContent>
								<SelectItem value="cash">{t("methodCash")}</SelectItem>
								<SelectItem value="bank_transfer">
									{t("methodBankTransfer")}
								</SelectItem>
								<SelectItem value="pos">{t("methodPos")}</SelectItem>
								<SelectItem value="other">{t("methodOther")}</SelectItem>
							</SelectContent>
						</Select>
					</div>
					<div>
						<Label>{`${t("amount")} (€)`}</Label>
						<Input
							type="number"
							min="0"
							step="0.01"
							className="rounded-sm"
							value={amount}
							onChange={(e) => setAmount(e.target.value)}
						/>
					</div>
					<div>
						<Label>{t("paymentDate")}</Label>
						<Input
							type="date"
							className="rounded-sm"
							value={date}
							onChange={(e) => setDate(e.target.value)}
						/>
					</div>
				</div>
				<DialogFooter>
					<Button variant="outline" className="rounded-sm" onClick={onClose}>
						{t("cancel")}
					</Button>
					<Button
						className="bg-volt hover:bg-volt/90 rounded-sm"
						disabled={saving}
						onClick={submit}
					>
						{t("save")}
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
}

function ProductDialog({ product, onClose, onSaved }) {
	const { t } = useLanguage();
	const isEdit = Boolean(product.product_id);
	const [name, setName] = useState(product.name || "");
	const [price, setPrice] = useState(
		product.unit_amount_cents
			? (product.unit_amount_cents / 100).toFixed(2)
			: "",
	);
	const [saving, setSaving] = useState(false);

	const submit = async () => {
		if (!name) {
			toast.error(t("productName"));
			return;
		}
		setSaving(true);
		try {
			if (isEdit) {
				await fetchAPI(`/products/${product.product_id}`, {
					method: "PATCH",
					body: JSON.stringify({ name, unit_amount_cents: toCents(price) }),
				});
			} else {
				await fetchAPI("/products", {
					method: "POST",
					body: JSON.stringify({ name, unit_amount_cents: toCents(price) }),
				});
			}
			toast.success(t("productSaved"));
			onSaved();
		} catch (err) {
			toast.error(err.detail || err.message);
		} finally {
			setSaving(false);
		}
	};

	return (
		<Dialog open onOpenChange={(o) => !o && onClose()}>
			<DialogContent className="max-w-md rounded-sm" {...NO_DISMISS_PROPS}>
				<DialogHeader>
					<DialogTitle className="font-heading">
						{isEdit ? t("editProduct") : t("newProduct")}
					</DialogTitle>
				</DialogHeader>
				<div className="space-y-4">
					<div>
						<Label>{t("productName")}</Label>
						<Input
							className="rounded-sm"
							value={name}
							onChange={(e) => setName(e.target.value)}
						/>
					</div>
					<div>
						<Label>{`${t("unitPrice")} (€)`}</Label>
						<Input
							type="number"
							min="0"
							step="0.01"
							className="rounded-sm"
							value={price}
							onChange={(e) => setPrice(e.target.value)}
						/>
					</div>
				</div>
				<DialogFooter>
					<Button variant="outline" className="rounded-sm" onClick={onClose}>
						{t("cancel")}
					</Button>
					<Button
						className="bg-volt hover:bg-volt/90 rounded-sm"
						disabled={saving}
						onClick={submit}
					>
						{t("save")}
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
}
